import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { first, forkJoin, of, shareReplay, switchMap, tap } from 'rxjs';
import { ProjectFormModel } from 'src/app/core/models/project/project-form.model';
import { ProjectService } from 'src/app/core/services/project.service';
import { EditableProjectComponent } from '../editable-project.component';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ProjectForUpsert } from 'src/app/core/models/project/project.model';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
import { Util } from 'src/app/core/utils/util';
import { OrganizationService } from 'src/app/core/services/organization.service';

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.component.html',
  styleUrls: ['./create-project.component.scss'],
  providers: [ProjectFormModel],
  standalone: false,
})
export class CreateProjectComponent
  extends EditableProjectComponent
  implements OnInit
{
  private readonly projectService = inject(ProjectService);
  private readonly employeeService = inject(EmployeeService);
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);

  @Input({ required: true })
  public companyId = '';
  @Output() public cancelled = new EventEmitter<void>();
  @Output() public created = new EventEmitter<string>();

  public ngOnInit(): void {
    this.projectImagePath = `companies/${this.companyId}/projects/${Util.generateGUID()}`;

    this.projectService.setProjectsUrl(this.companyId);
    this.employeeService.setEmployeesUrl(this.companyId);
    this.organizationService.setOrganizationsUrl(this.companyId);

    forkJoin({
      employees: this.employeeService.getCompanyEmployeesLookup(),
      organizations: this.organizationService.getMinimalOrganizations(),
    })
      .pipe(
        first(),
        tap(({ employees, organizations }) => {
          this.companyEmployees$.next(
            employees.filter(
              (e) => e.id !== this.authService.user$.getValue().data!.id,
            ),
          );

          this.companyOrganizations$.next(organizations);
        }),
      )
      .subscribe();
  }

  public submitForm(): void {
    const projectForInsert =
      this.projectFormModel.form.getRawValue() as ProjectForUpsert;

    projectForInsert.employeesIds = this.projectEmployees$
      .getValue()
      .map((e) => e.id);

    const extensionName = Util.getExtensionFromFileTitle(
      this.projectImage?.name,
    );
    const imageExtension = extensionName ? `.${extensionName}` : undefined;
    const projectId = this.projectImagePath.split('/').pop()!;
    const baseProjectUrl = `companies/${this.companyId}/projects`;

    projectForInsert.id = projectId;
    projectForInsert.titleImageExtension = imageExtension;

    this.projectService
      .createProject(projectForInsert)
      .pipe(
        first(),
        switchMap(() =>
          imageExtension
            ? this.fileService.deleteTemporaryFileInfo(
                `${baseProjectUrl}/${projectId}/title${imageExtension}`,
              )
            : of(null),
        ),
        tap(() => this.created.emit(projectId)),
      )
      .subscribe();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public onUploadFile(event: CustomFile): void {
    this.projectImage = event.file;

    this.previewUrl$ = ObservableUtil.getFilePreview$(this.projectImage).pipe(
      shareReplay(1),
    );
  }

  public onDeleteFile(): void {
    this.projectImage = undefined;
    this.previewUrl$ = of();
  }
}
